import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, CalendarRange, CheckSquare, Coins, KeyRound, Landmark, Lock, PiggyBank, ShieldCheck, Wallet, type LucideIcon } from "lucide-react";

import { AccessPanel } from "../components/governance/AccessPanel";
import { AccountsPanel, ApprovalsPanel, BudgetsPanel, GrantsPanel, OverviewPanel, PayPanel } from "../components/governance/FiguresPanels";
import { canSee, shortDate } from "../components/governance/governanceLogic";
import { PeriodBar } from "../components/financial/ReportDateBar";
import { quickPeriods } from "../components/financial/reportPeriods";
import { useAIStatusQuery } from "../hooks/useAIStatus";
import { usePermissions } from "../hooks/usePermissions";
import { financialService } from "../services/financialService";
import { apiErrorMessage } from "../utils/apiError";

type Tab = "overview" | "budgets" | "grants" | "approvals" | "accounts" | "pay" | "access";

const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

/**
 * Governance oversight: read-only financial figures for leadership, the
 * board and donors, each limited to what their access allows; and, for
 * whoever assigns permissions, who outside management may see what.
 */
export default function GovernanceDashboard() {
  const { hasCapability } = usePermissions();
  const canConfigure = hasCapability("core.assign_permissions");
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const today = localToday();
  const start = params.get("from") || `${today.slice(0, 4)}-01-01`;
  const end = params.get("to") || today;

  const dashboard = useQuery({
    queryKey: ["governance-dashboard", start, end],
    queryFn: () => financialService.getGovernanceDashboard({ date_from: start, date_to: end }),
    keepPreviousData: true,
    retry: (count, error) => (error as { response?: { status?: number } })?.response?.status !== 403 && count < 2,
  });
  const accounts = useQuery({ queryKey: ["governance-accounts"], queryFn: financialService.getGovernanceAccounts, enabled: canConfigure });
  const scopes = useQuery({ queryKey: ["governance-scopes"], queryFn: financialService.getGovernanceScopes, enabled: canConfigure });
  const grants = useQuery({ queryKey: ["grants"], queryFn: financialService.getGrants, enabled: canConfigure });
  const ai = useAIStatusQuery(true);
  const refreshAccess = () => Promise.all(["governance-accounts", "governance-scopes", "governance-dashboard"].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));

  const data = dashboard.data;
  const unset = (accounts.data ?? []).filter((row) => !row.scope).length;
  const tabs: { id: Tab; label: string; icon: LucideIcon; badge?: number }[] = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    ...(data?.budgets ? [{ id: "budgets" as Tab, label: "Budgets", icon: PiggyBank }] : []),
    ...(data && data.scope.grant_scope !== "none" ? [{ id: "grants" as Tab, label: "Grants", icon: Coins }] : []),
    ...(data?.approvals ? [{ id: "approvals" as Tab, label: "Approvals", icon: CheckSquare }] : []),
    ...(data?.financial_position?.accounts ? [{ id: "accounts" as Tab, label: "Accounts", icon: Landmark }] : []),
    ...(data?.salary_summary ? [{ id: "pay" as Tab, label: "Pay", icon: Wallet }] : []),
    ...(canConfigure ? [{ id: "access" as Tab, label: "Board and donor access", icon: KeyRound, badge: unset || undefined }] : []),
  ];
  const tab: Tab = tabs.find((item) => item.id === params.get("tab"))?.id ?? "overview";
  const setQuery = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };
  const go = (next: string) => setQuery({ tab: next === "overview" ? null : next });
  const period = data ? `${shortDate(data.period.start)} – ${shortDate(data.period.end)}` : "";
  const sees = data ? canSee({ ...data.scope, can_view_salary: Boolean(data.salary_summary) }) : [];
  const refused = dashboard.isError ? apiErrorMessage(dashboard.error, "The governance figures could not be loaded.") : "";
  const figureTab = tab !== "access";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700"><ShieldCheck className="h-5 w-5" /></span>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Governance oversight</h1>
            <p className="mt-1 text-sm text-gray-500">Read-only financial figures for leadership, the board and donors. Each person sees only what their access allows.</p>
          </div>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm">
          <span className="inline-flex items-center gap-1.5 font-medium text-gray-900"><Lock className="h-4 w-4 text-gray-500" /> Your view</span>
          <span className="text-gray-600">{sees.length ? sees.join(" · ") : "No figures yet"}</span>
          {(data.scope.valid_from || data.scope.valid_until) && (
            <span className="inline-flex items-center gap-1.5 text-gray-600"><CalendarRange className="h-4 w-4 text-gray-400" /> Access {data.scope.valid_from ? `from ${shortDate(data.scope.valid_from)} ` : ""}{data.scope.valid_until ? `until ${shortDate(data.scope.valid_until)}` : ""}</span>
          )}
        </div>
      )}

      {dashboard.isLoading && <div className="card p-10 text-center text-sm text-gray-500">Loading the governance figures…</div>}
      {refused && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800">
          <p className="font-medium">{refused}</p>
          {(dashboard.error as { response?: { status?: number } })?.response?.status === 403 ? <p className="mt-1 text-red-700">Ask the school's administration to renew or change your access.</p> : <button type="button" className="mt-2 font-medium underline" onClick={() => dashboard.refetch()}>Try again</button>}
        </div>
      )}

      {(data || canConfigure) && (
        <nav role="tablist" aria-label="Governance sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
          {tabs.map((item) => (
            <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} onClick={() => go(item.id)} className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${tab === item.id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}>
              <item.icon className="h-4 w-4" /> {item.label}
              {item.badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{item.badge}</span> : null}
            </button>
          ))}
        </nav>
      )}

      {data && figureTab && ["overview", "approvals", "accounts"].includes(tab) && (
        <div className={`card card-content pt-5 transition-opacity ${dashboard.isFetching ? "opacity-60" : ""}`}>
          <PeriodBar
            start={data.period.start}
            end={data.period.end}
            periods={quickPeriods(today)}
            onChange={(from, to) => setQuery({ from, to })}
            hint="Balances are as at the last day. Income, spending and approvals cover the whole period. The period cannot run past today."
          />
        </div>
      )}

      {data && tab === "overview" && <OverviewPanel data={data} go={go} canWrite={Boolean(ai.data?.can_assist)} />}
      {data?.budgets && tab === "budgets" && <BudgetsPanel rows={data.budgets} today={today} drillDown={data.scope.can_drill_down} />}
      {data && tab === "grants" && <GrantsPanel rows={data.grants} today={today} selectedOnly={data.scope.grant_scope === "selected"} />}
      {data?.approvals && tab === "approvals" && <ApprovalsPanel approvals={data.approvals} period={period} />}
      {data?.financial_position?.accounts && tab === "accounts" && <AccountsPanel rows={data.financial_position.accounts} asOf={data.period.end} period={period} />}
      {data?.salary_summary && tab === "pay" && <PayPanel summary={data.salary_summary} />}
      {canConfigure && tab === "access" && (
        <AccessPanel
          accounts={accounts.data}
          scopes={scopes.data}
          grants={grants.data ?? []}
          isLoading={accounts.isLoading || scopes.isLoading}
          isError={accounts.isError || scopes.isError}
          onRetry={() => { accounts.refetch(); scopes.refetch(); }}
          onChanged={refreshAccess}
        />
      )}
    </div>
  );
}
